import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  signal,
  Signal, WritableSignal,
} from '@angular/core';
import { Observable, share, take } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { routerSegments } from '@consts/routes';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MenuSelectors } from '@states/menu/menu.selector-types';
import { MenuKey } from '@enums/menu.enum';
import { CustomEventsActions } from '@states/custom-events/custom-events.action-types';
import { KeyValuePairs } from '../../core/interfaces';
import { OrgGroupModels } from '@models/org-group.model';
import { OrgGroupsSelectors } from '@states/org-groups/org-groups.selector-types';
import { UiOrgGroupDialogComponent, UiOrgGroupDialogData, UiOrgGroupDialogResult } from '../../shared/ui-kit/ui-org-group-dialog/ui-org-group-dialog.component';
import { OrgGroupsActions } from '@states/org-groups/org-groups.action-types';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogService } from '../../shared/confirm-dialog/confirm-dialog.service';
import { ConfirmDialogModel } from '@ui';
import { PermissionModel } from '@models/permission.model';
import { UserSelectors } from '@states/user/user.selector-types';
import { CustomEventModel } from '@models/custom-event.model';
import { CustomEventsSelectors } from '@states/custom-events/custom-events.selector-types';
import { UiBreadCrumbItem } from '@models/route.models';
import { ScheduleModel } from '@models/schedule.model';
import { ScheduleSelectors } from '@states/schedule/schedule.selector-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { LetDirective } from '@ngrx/component';
import { UiKitModule } from '../../shared/ui-kit/ui-kit.module';
import { AsyncPipe, NgIf } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { RbacOrgAllowDirective } from '../../authorization/rbac-org-allow.directive';
import { MatIcon } from '@angular/material/icon';
import { UiButtonComponent, UiHeaderComponent } from '@ui';
import { CustomObjectsActions } from '@states/custom-objects/custom-objects.action-types';
import { UiCustomObjectTrainingDialogService } from '../../shared/ui-kit/ui-custom-object-training-dialog/ui-custom-object-training-dialog.service';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { CustomObjectsSelectors } from '@states/custom-objects/custom-objects.selector-types';
import { CustomObjectModels } from '@models/custom-object.model';
import { IfDeveloperDirective } from '../../directives/if-developer.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatabaseTagsSelectors } from '@states/database-tags/database-tags.selector-types';
import { DatabaseTagsModel } from '@models/database-tags.model';
import { Dictionary } from '@ngrx/entity/src/models';
import { MapCustomObjectsActions } from '@states/map-custom-objects/map-custom-objects.action-types';
import { CustomObjectStatesSelectors } from '@states/custom-objects-states/custom-objects-states.selector-types';
import { zoneRegionsFeature } from '@states/zone-regions/zone-regions.feature';
import { contactGroupsFeature } from '@states/contact-groups/contact-groups.feature';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { vehicleGroupsFeature } from '../../store/states/vehicle-groups/vehicle-groups.feature';

@Component({
  selector: 'app-database-settings',
  templateUrl: './database.component.html',
  styleUrls: ['./database.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LetDirective, UiKitModule, NgIf, TranslatePipe, MatTooltip, AsyncPipe, RbacOrgAllowDirective, MatIcon, RouterLink, RouterLinkActive, RouterOutlet, UiHeaderComponent, UiButtonComponent, IfDeveloperDirective],
})
export class DatabaseComponent implements OnInit {
  public isFaceRecognitionEnabled$: Observable<boolean> = this.store$.pipe(
    select(LocationSelectors.isFaceRecognitionEnabled, share()),
  );

  public selectDatabaseTagEntities: Signal<Dictionary<DatabaseTagsModel.DatabaseTagDocument>> =
    this.store$.selectSignal(DatabaseTagsSelectors.selectDatabaseTagEntities);

  public selectZoneRegionEntities = this.store$.selectSignal(zoneRegionsFeature.selectEntities);
  public selectContactGroupEntities = this.store$.selectSignal(contactGroupsFeature.selectContactGroupEntities);
  public selectPersonGroupEntities = this.store$.selectSignal(
    personGroupsFeature.selectPersonGroupEntities,
  );
  private readonly selectPersonGroupDetailsState = this.store$.selectSignal(
    personGroupsFeature.selectPersonGroupDetails,
  );
  public selectVehicleGroupEntities = this.store$.selectSignal(
    vehicleGroupsFeature.selectVehicleGroupEntities,
  );
  private readonly selectVehicleGroupDetailsState = this.store$.selectSignal(
    vehicleGroupsFeature.selectVehicleGroupDetails,
  );

  public groupType = OrgGroupModels.OrgGroupType;

  public MenuKey = MenuKey;

  public collapsed = false;
  public selectIsDevTeam$: Observable<boolean> = this.store$.pipe(select(UserSelectors.isDevTeam));
  public selectIsDeveloper$: Observable<boolean> = this.store$.pipe(select(UserSelectors.isDeveloper));
  public enableShelves$: Observable<boolean> = this.store$.pipe(select(OrganizationSelectors.selectEnableShelves));
  public selectActiveLevel2$: Observable<MenuKey> = this.store$.pipe(
    select(MenuSelectors.selectActiveLevel2),
  );
  public routerSegments = routerSegments;

  public breadCrumbs: UiBreadCrumbItem[] = [{ name: 'Organization database' }];

  public menuExp: KeyValuePairs<boolean> = {
    [OrgGroupModels.OrgGroupType.Persons]: false,
    [OrgGroupModels.OrgGroupType.Doors]: false,
    [OrgGroupModels.OrgGroupType.Vehicles]: false,
  };
  public isContactsDirectoryExpanded = false;

  public orgGroupName = '';
  public orgGroupType: OrgGroupModels.OrgGroupType;

  public orgGroup: OrgGroupModels.OrgGroup;
  public permissions = PermissionModel.Permissions;
  public selectAllCustomEvents$: Observable<CustomEventModel.CustomEvent[]> = this.store$.pipe(
    select(CustomEventsSelectors.selectAllCustomEvents),
  );
  public selectSelectedSchedule$: Observable<ScheduleModel.ScheduleDocument> = this.store$.pipe(
    select(ScheduleSelectors.selectSelectedSchedule),
  );

  public selectCustomObjectsEnabled$: Observable<boolean> = this.store$.pipe(
    select(OrganizationSelectors.selectCustomObjectsEnabled),
  );

  public selectAllCustomObjects$: Observable<CustomObjectModels.CustomObject[]> = this.store$.pipe(
    select(CustomObjectsSelectors.selectAllCustomObjects),
  );

  public selectAllCustomObjectsStates$: Observable<CustomObjectModels.StateCustomObject[]> = this.store$.pipe(
    select(CustomObjectStatesSelectors.selectAllCustomObjectStates),
  );

  constructor(
    private confirm: ConfirmDialogService,
    private dialog: MatDialog,
    private store$: Store,
    private router: Router,
    private route: ActivatedRoute,
    private trainingDialogService: UiCustomObjectTrainingDialogService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
  ) {}

  buildBreadcrumb(url: string) {
    const breadCrumbs: UiBreadCrumbItem[] = [{ name: 'Organization database' }];
    if (url.includes(routerSegments.people)) {
      breadCrumbs.push({ name: 'People' });
    }
    if (url.includes(routerSegments.uniform)) {
      breadCrumbs.push({ name: 'Uniforms' });
    }
    if (url.includes(routerSegments.customObjects)) {
      breadCrumbs.push({ name: 'Custom objects' });
    }
    if (url.includes(routerSegments.vehicles)) {
      breadCrumbs.push({ name: 'Vehicles' });
    }
    if (url.includes(routerSegments.doors)) {
      breadCrumbs.push({ name: 'Doors' });
    }
    if (url.includes(routerSegments.youtube)) {
      breadCrumbs.push({ name: 'Youtube' });
    }
    if (url.includes(routerSegments.personGroups)) {
      const personGroupMatch = url.match(/\/person-groups\/([^/?]+)/);
      const personGroupId = personGroupMatch?.[1];
      const personGroupsLabel = this.translateService.instant('DATABASE.PERSON_GROUPS');

      if (personGroupId) {
        const groupName = this.resolvePersonGroupName(personGroupId);

        breadCrumbs.push({
          name: personGroupsLabel,
          route: `/${routerSegments.database}/${routerSegments.personGroups}`,
        });

        breadCrumbs.push({ name: groupName || personGroupId });
      } else {
        breadCrumbs.push({
          name: personGroupsLabel,
        });
      }
    }
    if (url.includes(routerSegments.vehicleGroups)) {
      const vehicleGroupMatch = url.match(/\/vehicle-groups\/([^/?]+)/);
      const vehicleGroupId = vehicleGroupMatch?.[1];
      const vehicleGroupsLabel = this.translateService.instant('DATABASE.VEHICLE_GROUPS');

      if (vehicleGroupId) {
        const groupName = this.resolveVehicleGroupName(vehicleGroupId);

        breadCrumbs.push({
          name: vehicleGroupsLabel,
          route: `/${routerSegments.database}/${routerSegments.vehicleGroups}`,
        });

        breadCrumbs.push({ name: groupName || vehicleGroupId });
      } else {
        breadCrumbs.push({
          name: vehicleGroupsLabel,
        });
      }
    }
    const isOrgGroupsRoute = new RegExp(`/${routerSegments.groups}(/|$)`).test(url);
    if (isOrgGroupsRoute) {
      breadCrumbs.push({ name: 'Groups' });
      if (this.orgGroupName) {
        breadCrumbs.pop();
        breadCrumbs.push({
          name: 'Groups',
          route: `/${routerSegments.database}/${routerSegments.groups}/${this.orgGroupType}`,
        });
        breadCrumbs.push({ name: this.orgGroupName });
      }
    }
    if (url.includes(routerSegments.eventTypes)) {
      breadCrumbs.push({
        name: 'Event tags',
        route: `/${routerSegments.database}/${routerSegments.eventTypes}`,
      });
    }
    if (url.includes('schedule/create')) {
      breadCrumbs.push({
        name: 'Schedule table',
        route: `/${routerSegments.database}/${routerSegments.schedule}`,
      });
    }
    if (url.includes(routerSegments.tags)) {
      const tagMatch = url.match(/\/tags\/([^/]+)/);
      const tagId = tagMatch?.[1];

      if (tagId) {
        const tagName = this.selectDatabaseTagEntities()[tagId]?.name || '';

        breadCrumbs.push({
          name: this.translateService.instant('DATABASE_TAGS.BREADCRUMBS.TAGS'),
          route: `/${routerSegments.database}/${routerSegments.tags}`,
        });

        breadCrumbs.push({ name: tagName });
      } else {
        breadCrumbs.push({
          name: this.translateService.instant('DATABASE_TAGS.BREADCRUMBS.TAGS'),
        });
      }
    }
    if (url.includes(routerSegments.shelfs)) {
      breadCrumbs.push({
        name: this.translateService.instant('DATABASE.SHELFS'),
      });
    }
    if (url.includes(routerSegments.workflowWebhooks)) {
      breadCrumbs.push({
        name: this.translateService.instant('DATABASE.WEBHOOK'),
      });
    }
    if (url.includes(routerSegments.categories)) {
      breadCrumbs.push({
        name: this.translateService.instant('DATABASE.CATEGORIES'),
      });
    }
    if (url.includes(routerSegments.create) && !(url.includes(routerSegments.tags) || url.includes(routerSegments.shelfs) || url.includes(routerSegments.categories))) {
      breadCrumbs.push({ name: 'Create' });
    }
    if (url.includes(routerSegments.edit) && !url.includes(routerSegments.tags)) {
      breadCrumbs.push({ name: 'Edit' });
    }
    if (url.includes(routerSegments.mapObjects)) {
      breadCrumbs.push({ name: this.translateService.instant('ORG_SETTINGS.MENU.CUSTOM_OBJECTS_MAP') });
    }
    if (url.includes(routerSegments.contacts)) {
      breadCrumbs.push({ name: this.translateService.instant('ORG_SETTINGS.MENU.CONTACTS') });
    }
    if (url.includes(routerSegments.contactGroups)) {
      const contactGroupMatch = url.match(/\/contact-groups\/([^/?]+)/);
      const contactGroupId = contactGroupMatch?.[1];
      const contactsLabel = this.translateService.instant('ORG_SETTINGS.MENU.CONTACTS');
      const groupsLabel = this.translateService.instant('CONTACT_GROUPS.MENU_TITLE');

      breadCrumbs.push({
        name: contactsLabel,
        route: `/${routerSegments.database}/${routerSegments.contacts}`,
      });

      if (contactGroupId) {
        const groupName =
          this.selectContactGroupEntities()[contactGroupId]?.name ??
          '';

        breadCrumbs.push({
          name: groupsLabel,
          route: `/${routerSegments.database}/${routerSegments.contactGroups}`,
        });

        breadCrumbs.push({ name: groupName || contactGroupId });
      } else {
        breadCrumbs.push({
          name: groupsLabel,
        });
      }
    }
    if (url.includes(routerSegments.zoneRegions)) {
      const zoneRegionMatch = url.match(/\/zone-regions\/([^/?]+)/);
      const zoneRegionId = zoneRegionMatch?.[1];

      if (zoneRegionId) {
        const zoneRegionName = this.selectZoneRegionEntities()[zoneRegionId]?.name || '';

        breadCrumbs.push({
          name: this.translateService.instant('DATABASE.ZONE_REGIONS'),
          route: `/${routerSegments.database}/${routerSegments.zoneRegions}`,
        });

        breadCrumbs.push({ name: zoneRegionName });
      } else {
        breadCrumbs.push({ name: this.translateService.instant('DATABASE.ZONE_REGIONS') });
      }
    }
    this.breadCrumbs = breadCrumbs;
  }

  private resolvePersonGroupName(groupId: string): string {
    const details = this.selectPersonGroupDetailsState().document;
    if (details?._id === groupId) {
      return details.name;
    }
    return this.selectPersonGroupEntities()[groupId]?.name ?? '';
  }

  private resolveVehicleGroupName(groupId: string): string {
    const details = this.selectVehicleGroupDetailsState().document;
    if (details?._id === groupId) {
      return details.name;
    }
    return this.selectVehicleGroupEntities()[groupId]?.name ?? '';
  }

  ngOnInit(): void {
    this.buildBreadcrumb(this.router.url);
    if (this.router.url.includes(OrgGroupModels.OrgGroupType.Persons)) {
      this.isFaceRecognitionEnabled$.pipe(take(1)).subscribe((enabled) => {
        if (!enabled) {
          this.router.navigate([routerSegments.database, routerSegments.vehicles]);
          return;
        }
      });
      this.menuExp[OrgGroupModels.OrgGroupType.Persons] = true;
    }
    if (this.router.url.includes(OrgGroupModels.OrgGroupType.Vehicles)) {
      this.menuExp[OrgGroupModels.OrgGroupType.Vehicles] = true;
    }
    if (this.router.url.includes(OrgGroupModels.OrgGroupType.Doors)) {
      this.menuExp[OrgGroupModels.OrgGroupType.Doors] = true;
    }
    if (
      this.router.url.includes(routerSegments.contacts) ||
      this.router.url.includes(routerSegments.contactGroups)
    ) {
      this.isContactsDirectoryExpanded = true;
    }
    this.store$
      .select(OrgGroupsSelectors.selectSelectedOrgGroup)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((orgGroup) => {
        if (!orgGroup) {
          this.orgGroupName = '';
          delete this.orgGroupType;
        } else {
          this.orgGroupName = orgGroup?.name;
          this.orgGroupType = orgGroup?.type;
          this.orgGroup = orgGroup;
        }
        this.buildBreadcrumb(this.router.url);
      });
    this.store$.select(zoneRegionsFeature.selectZoneRegions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildBreadcrumb(this.router.url);
        this.cdr.detectChanges();
      });

    this.store$
      .select(contactGroupsFeature.selectContactGroups)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildBreadcrumb(this.router.url);
        this.cdr.detectChanges();
      });

    this.store$
      .select(personGroupsFeature.selectPersonGroupEntities)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildBreadcrumb(this.router.url);
        this.cdr.detectChanges();
      });

    this.store$
      .select(personGroupsFeature.selectPersonGroupDetails)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildBreadcrumb(this.router.url);
        this.cdr.detectChanges();
      });

    this.store$
      .select(vehicleGroupsFeature.selectVehicleGroupEntities)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildBreadcrumb(this.router.url);
        this.cdr.detectChanges();
      });

    this.store$
      .select(vehicleGroupsFeature.selectVehicleGroupDetails)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildBreadcrumb(this.router.url);
        this.cdr.detectChanges();
      });

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = event.url;
        this.buildBreadcrumb(url);
      }
    });

    // Subscribe to activeLevel2 changes to trigger change detection
    this.selectActiveLevel2$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Use setTimeout to ensure change detection runs after the state update is complete
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    });
  }

  public toggleMenu() {
    this.collapsed = !this.collapsed;
  }

  public toggleSubmenu(type: OrgGroupModels.OrgGroupType) {
    this.menuExp[type] = !this.menuExp[type];
  }

  public toggleContactsDirectorySubmenu(): void {
    this.isContactsDirectoryExpanded = !this.isContactsDirectoryExpanded;
  }

  public createEventType() {
    this.store$.dispatch(CustomEventsActions.pressSave());
  }

  public addEditGroup(orgGroup?: OrgGroupModels.OrgGroup) {
    const data: UiOrgGroupDialogData = {
      orgGroup: orgGroup ?? {
        name: '',
        type: this.orgGroupType,
        members: [],
      },
    };
    this.dialog
      .open(UiOrgGroupDialogComponent, {
        width: '572px',
        panelClass: 'modal-no-padding',
        data,
      })
      .afterClosed()
      .subscribe((result: UiOrgGroupDialogResult) => {
        if (!result) {
          return;
        }
        if (orgGroup) {
          this.store$.dispatch(OrgGroupsActions.editOrgGroup({ orgGroup: result.orgGroup }));
        } else {
          this.store$.dispatch(OrgGroupsActions.addOrgGroup({ orgGroup: result.orgGroup }));
        }
      });
  }

  public deleteGroup(orgGroup?: OrgGroupModels.OrgGroup) {
    this.confirm
      .open({
        title: `Delete ${orgGroup.name}?`,
        msg: `Are you sure you want to delete ${orgGroup.name}?`,
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        confirm: 'Delete',
        cancel: 'No',
      })
      .subscribe((res) => {
        if (res.selection === 1) {
          this.store$.dispatch(OrgGroupsActions.removeOrgGroup({ orgGroup }));
          this.router.navigate([routerSegments.groups, orgGroup.type], { relativeTo: this.route });
        }
      });
  }

  public onCreateCustomObject(): void {
    this.trainingDialogService.openTrainingDialog()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result && result.saved) {
          console.log('Custom object created:', result);
          // Refresh the custom objects list
          this.store$.dispatch(CustomObjectsActions.getSavedCustomObjectStates());
        }
      });
  }

  public onCreateMapCustomObject(): void {
    this.trainingDialogService.openCreatingMapObjectDialog()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res) {
          this.store$.dispatch(MapCustomObjectsActions.setMarkerCoords({ coords: res.coords }));
        }
      });
  }
}
